/**
 * Pure Dockerfile synthesis and content-addressed identity for DockerTemplate.
 *
 * A `DockerTemplate` records an ordered list of operations (setWorkdir, setEnvs,
 * runCmd, runWithSecrets, aptInstall, pipInstall, npmInstall) over a base image. This module
 * turns that ordered list into a deterministic Dockerfile string and a stable
 * content hash. It performs no I/O, so it is fully unit-testable without a
 * Docker daemon.
 */

import { createHash } from 'node:crypto';

// =============================================================================
// Operations
// =============================================================================

export interface AptInstallOptions {
  /** Pass `--no-install-recommends` to `apt-get install`. */
  noInstallRecommends?: boolean;
  /** Pass `--fix-missing` to `apt-get install`. */
  fixMissing?: boolean;
}

export interface PipInstallOptions {
  /** Install system-wide (default). `false` installs for the current user (`--user`). */
  g?: boolean;
}

export interface NpmInstallOptions {
  /** Install globally (`npm install -g`). */
  g?: boolean;
  /** Include dev dependencies (`--include=dev`). Ignored when `packages` is set. */
  dev?: boolean;
}

export interface RunWithSecretsOptions {
  /**
   * Names of environment variables whose values are read from the building
   * process's `process.env` at `build()` time and exposed to the command. Only
   * the names participate in the template identity.
   */
  secrets: string[];
  /**
   * Absolute path produced by the command. Only this path is copied into the
   * template image; everything else the command does (including the secret
   * values) stays in a throwaway build stage.
   */
  output: string;
  /**
   * Owner of the copied output, as `user[:group]` or `uid[:gid]`. Set this when
   * the base image runs as a non-root `USER`; otherwise the output is owned by root.
   */
  owner?: string;
}

export type DockerTemplateOperation =
  | { method: 'setWorkdir'; args: [string] }
  | { method: 'setEnvs'; args: [Record<string, string>] }
  | { method: 'runCmd'; args: [string | string[]] }
  | { method: 'runWithSecrets'; args: [string | string[], RunWithSecretsOptions] }
  | { method: 'aptInstall'; args: [string | string[], AptInstallOptions?] }
  | { method: 'pipInstall'; args: [(string | string[])?, PipInstallOptions?] }
  | { method: 'npmInstall'; args: [(string | string[])?, NpmInstallOptions?] };

/** A fully-resolved template definition: the base image and the ordered operations. */
export interface DockerTemplateDefinition {
  baseImage: string;
  operations: readonly DockerTemplateOperation[];
}

// =============================================================================
// Dockerfile synthesis
// =============================================================================

function toCommandList(command: string | string[]): string[] {
  return Array.isArray(command) ? command : [command];
}

function sortedEntries(record: Record<string, string>): Array<[string, string]> {
  return Object.entries(record).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
}

function renderEnvLine(envs: Record<string, string>): string | undefined {
  const pairs = sortedEntries(envs);
  if (pairs.length === 0) return undefined;
  // Deterministic order so identical envs always render identically.
  return `ENV ${pairs.map(([key, value]) => `${key}=${JSON.stringify(value)}`).join(' ')}`;
}

function renderAptInstall(packages: string | string[], options?: AptInstallOptions): string {
  const flags: string[] = [];
  if (options?.noInstallRecommends) flags.push('--no-install-recommends');
  if (options?.fixMissing) flags.push('--fix-missing');
  const flagStr = flags.length > 0 ? `${flags.join(' ')} ` : '';
  const pkgs = toCommandList(packages).join(' ');
  return `RUN apt-get update && apt-get install -y ${flagStr}${pkgs} && rm -rf /var/lib/apt/lists/*`;
}

function renderPipInstall(packages?: string | string[], options?: PipInstallOptions): string {
  const userFlag = options?.g === false ? ' --user' : '';
  const target = packages === undefined ? '.' : toCommandList(packages).join(' ');
  return `RUN pip install${userFlag} ${target}`;
}

function renderNpmInstall(packages?: string | string[], options?: NpmInstallOptions): string {
  const flags: string[] = [];
  if (options?.g) flags.push('-g');
  if (packages === undefined) {
    if (options?.dev) flags.push('--include=dev');
    const flagStr = flags.length > 0 ? ` ${flags.join(' ')}` : '';
    return `RUN npm install${flagStr}`;
  }
  const flagStr = flags.length > 0 ? ` ${flags.join(' ')}` : '';
  const pkgs = toCommandList(packages).join(' ');
  return `RUN npm install${flagStr} ${pkgs}`;
}

function secretStageName(index: number): string {
  return `mastra-secret-${index}`;
}

function mainStageName(index: number): string {
  return `mastra-main-${index}`;
}

/**
 * Render a deterministic Dockerfile from a template definition.
 *
 * Operations accumulate in a chain of "main" stages. Every `runWithSecrets`
 * operation forks a throwaway stage from the main stage as it stands at that
 * point (so it sees earlier WORKDIR/ENV/installs) and runs the command with
 * each secret exposed through a BuildKit secret mount, exported into the
 * command's environment for that single RUN. The next main stage then starts
 * from the same pre-secret snapshot and `COPY --from`s only the declared
 * output path. Secret mounts are tmpfs-backed and never part of a layer, its
 * history, or the build cache; the throwaway stage additionally keeps anything
 * else the command wrote (caches, logs) out of the template image.
 */
/**
 * `RUN --mount=type=secret,id=X,mode=0444 ... export X="$(cat /run/secrets/X)" && <command>`.
 * Reading the file into a shell variable keeps the value visible only to this
 * RUN's process tree, without depending on the newer `env=` mount option.
 * `mode=0444` because BuildKit's default is `0400 root`, which a base image
 * with a non-root `USER` cannot read; world-readable within the RUN is no
 * wider than the exported variable already is.
 */
function renderSecretRun(command: string | string[], secrets: string[]): string {
  const names = [...secrets].sort();
  const mounts = names.map(name => `--mount=type=secret,id=${name},mode=0444`);
  const exports = names.map(name => `${name}="$(cat /run/secrets/${name})"`);
  const prefix = names.length > 0 ? [`export ${exports.join(' ')}`] : [];
  return `RUN ${[...mounts, ''].join(' ')}${[...prefix, ...toCommandList(command)].join(' && ')}`;
}

const OWNER_PATTERN = /^[A-Za-z0-9_.-]+(:[A-Za-z0-9_.-]+)?$/;

/** Throws unless `owner` is a `user[:group]` or `uid[:gid]` usable in `COPY --chown`. */
export function assertValidOwner(owner: unknown): asserts owner is string {
  if (typeof owner !== 'string' || !OWNER_PATTERN.test(owner)) {
    throw new TypeError(`owner must be user[:group] or uid[:gid], got ${JSON.stringify(owner)}`);
  }
}

export function synthesizeDockerfile(definition: DockerTemplateDefinition): string {
  const lines: string[] = [];
  let mainIndex = 0;
  const openMain = (from: string) => {
    lines.push(`FROM ${from} AS ${mainStageName(mainIndex)}`);
  };
  openMain(definition.baseImage);

  definition.operations.forEach((operation, index) => {
    switch (operation.method) {
      case 'setWorkdir':
        lines.push(`WORKDIR ${operation.args[0]}`);
        break;
      case 'setEnvs': {
        const line = renderEnvLine(operation.args[0]);
        if (line) lines.push(line);
        break;
      }
      case 'runCmd':
        lines.push(`RUN ${toCommandList(operation.args[0]).join(' && ')}`);
        break;
      case 'runWithSecrets': {
        const [command, { secrets, output, owner }] = operation.args;
        if (owner !== undefined) assertValidOwner(owner);
        const snapshot = mainStageName(mainIndex);
        const stage = secretStageName(index);
        lines.push(`FROM ${snapshot} AS ${stage}`, renderSecretRun(command, secrets));
        mainIndex += 1;
        openMain(snapshot);
        lines.push(`COPY ${owner !== undefined ? `--chown=${owner} ` : ''}--from=${stage} ${output} ${output}`);
        break;
      }
      case 'aptInstall':
        lines.push(renderAptInstall(operation.args[0], operation.args[1]));
        break;
      case 'pipInstall':
        lines.push(renderPipInstall(operation.args[0], operation.args[1]));
        break;
      case 'npmInstall':
        lines.push(renderNpmInstall(operation.args[0], operation.args[1]));
        break;
    }
  });

  return `${lines.join('\n')}\n`;
}

/** Names of every secret referenced by `runWithSecrets` operations, deduplicated and sorted. */
export function secretNames(definition: DockerTemplateDefinition): string[] {
  const names = new Set<string>();
  for (const operation of definition.operations) {
    if (operation.method === 'runWithSecrets') {
      for (const name of operation.args[1].secrets) names.add(name);
    }
  }
  return [...names].sort();
}

// =============================================================================
// Content-addressed identity
// =============================================================================

/** Prefix for template image tags built locally. */
export const TEMPLATE_IMAGE_REPO = 'mastra-template';

function canonicalOperation(operation: DockerTemplateOperation): unknown {
  switch (operation.method) {
    case 'setEnvs':
      return { method: 'setEnvs', args: [sortedEntries(operation.args[0])] };
    case 'runWithSecrets': {
      const [command, { secrets, output, owner }] = operation.args;
      return {
        method: 'runWithSecrets',
        args: [command, { secrets: [...secrets].sort(), output, ...(owner !== undefined ? { owner } : {}) }],
      };
    }
    default:
      return operation;
  }
}

/**
 * Stable content hash over the base image and ordered operations. Env records
 * and secret name lists are canonicalized so insertion order does not change
 * the identity. Secret *values* never enter the definition, so the same
 * definition built with different credentials resolves to the same image tag.
 */
export function templateIdentity(definition: DockerTemplateDefinition): string {
  const canonical = JSON.stringify({
    schemaVersion: 1,
    baseImage: definition.baseImage,
    operations: definition.operations.map(canonicalOperation),
  });
  return createHash('sha256').update(canonical).digest('hex').slice(0, 24);
}

/** Full `mastra-template:<hash>` tag for a definition. */
export function templateImageTag(definition: DockerTemplateDefinition): string {
  return `${TEMPLATE_IMAGE_REPO}:${templateIdentity(definition)}`;
}
